import {filter, firstValueFrom, map, of, Subject, timeout} from 'rxjs';
import {v4 as uuid} from 'uuid';

import {
  ClientErrorResponse,
  ClientResponse,
  Conversation,
  ConversationContext,
  ConversationParticipant,
  initObservability,
  Observability,
  REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
  RequestAction,
} from '@stripe-internal/conversations-client';
import {createStore} from '@stripe-internal/state-manager';
import {
  InlineDataEventBus,
  InlineDataService,
} from '@stripe-internal/inline-data';
import {
  MessageFromParent,
  MessageFromParentType,
  MessageToParentType,
  RequestedAction,
  Visibility,
} from '@stripe-internal/support-conversations-controller';
import {TwilioClient} from '@stripe-internal/twilio-conversations-client';
import {UniversalHelpClient} from '@stripe-internal/universal-help-conversations-client';

import {Api} from './lib/api/api';
import {
  App,
  AppBusMessage,
  AppConfig,
  AppProfile,
  AppState,
  AppStateStore,
} from './types';
import {ClientFetch} from './lib/util/clientFetch';
import {createApi} from './lib/api/createApi';
import {createClient} from './lib/createClient';
import {createParentMessageBus} from './lib/parentMessageBus/createParentMessageBus';
import {devLog} from './lib/util/dev';
import {isStartFreshTriggerPoint} from './lib/util/triggerPoints';
import {newInlineDataService} from './lib/inlineDataService';
import {ParentMessageBus} from './lib/parentMessageBus/ParentMessageBus';
import {SupportConversationsClient} from './lib/SupportConversationsClient';
import {SupportTriggerPointTriggerPoint} from '../generated/types';
import {wrapFetch} from './lib/wrapFetch';

export class AppImpl implements App {
  private _stateStore: AppStateStore;

  private _api: Api;

  private _client: SupportConversationsClient;

  private _chatClient: TwilioClient;

  private _uhClient: UniversalHelpClient;

  private _inlineDataEventBus = new InlineDataEventBus();

  private _inlineDataService: InlineDataService;

  private _parentMessageBus: ParentMessageBus;

  private _parentMessageSubject = new Subject<AppBusMessage>();

  private _observability: Observability;

  private _handleRequestedAction:
    | ((action: RequestedAction) => void)
    | undefined;

  private _previousTriggerPoint?: string;

  private _conversationMap = new Map<string, Conversation>();

  constructor(
    config: AppConfig,
    profile: AppProfile,
    clientFetch: ClientFetch,
    handleRequestedAction?: (action: RequestedAction) => void,
    parentMessageBus?: ParentMessageBus,
    observability: Partial<Observability> = {},
  ) {
    const {triggerPoint} = config;
    this._stateStore = createStore<AppState>({
      initialized: false,
      currentConversationId: null,
      config,
      profile,
      triggerParams: triggerPoint ? {triggerPoint} : undefined,
      visibility: (config.initialVisibility as Visibility) ?? 'open',
    });

    const getConfig = () => this.config;
    const getProfile = () => this.profile;
    const wrappedFetch = wrapFetch(clientFetch, getConfig, getProfile);
    this._api = createApi(wrappedFetch, getProfile);

    this._handleRequestedAction = handleRequestedAction;

    this._inlineDataService = newInlineDataService(wrappedFetch, {
      audience: this.config.audience,
      entry_point: this.config.entryPoint,
    });

    this._parentMessageBus =
      parentMessageBus ??
      createParentMessageBus({
        audience: config.audience,
        entryPoint: config.entryPoint,
        parentOrigin: config.parentOrigin ?? window.origin,
      });

    this._observability = initObservability(observability);

    const getApi = () => this.api;
    const requestAction = (action: RequestAction) => this.requestAction(action);
    const getObservability = () => this._observability;

    const {client, chatClient, uhClient} = createClient({
      getConfig,
      getProfile,
      getApi,
      getObservability,
      inlineDataEventBus: this.inlineDataEventBus,
      requestAction,
    });
    this._client = client;
    this._chatClient = chatClient;
    this._uhClient = uhClient;

    this.stateStore.setValue((state) => {
      state.initialized = true;
    });

    this.handleParentMessage = this.handleParentMessage.bind(this);

    devLog('App initialized with config:', config);
    this.parentMessageBus.post({
      type: MessageToParentType.SetWidgetInitialized,
    });
  }

  get initialized() {
    return this.state.initialized;
  }

  get stateStore() {
    return this._stateStore;
  }

  get state() {
    return this.stateStore.getState();
  }

  get config() {
    return this.state.config;
  }

  get profile() {
    return this.state.profile;
  }

  hasFlag(name: string) {
    return !!this.state.profile.featureFlags[name];
  }

  get api() {
    return this._api;
  }

  get client() {
    return this._client;
  }

  // PLEASE DO NOT USE DIRECTLY. This is only exposed to bind to STP
  get chatClient() {
    return this._chatClient;
  }

  get inlineDataEventBus() {
    return this._inlineDataEventBus;
  }

  get inlineDataService() {
    return this._inlineDataService;
  }

  get parentMessageBus() {
    return this._parentMessageBus;
  }

  subscribeToParentMessageBus() {
    this.parentMessageBus.addListener(this.handleParentMessage);
  }

  unsubscribeFromParentMessageBus() {
    this.parentMessageBus.removeListener(this.handleParentMessage);
  }

  // maybe this should just be a part of parentMessageBus
  requestAction(action: RequestAction) {
    // the nonce will associate a response with our request
    const nonce = uuid();

    const {waitForResponse, responseTimeout, action: requestedAction} = action;

    if (this._handleRequestedAction) {
      this._handleRequestedAction(requestedAction);
      // faking a response value for now
      return Promise.resolve({code: 200});
    }

    const parentMessagesObs = this._parentMessageSubject.asObservable();

    let response: Promise<ClientResponse>;

    if (waitForResponse) {
      const timeoutResponse = {
        code: 400,
        error: `Timeout waiting for request-action response for ${action.action.name}`,
      };

      response = firstValueFrom(
        parentMessagesObs.pipe(
          // find the response message that matches our request nonce
          filter((m) => m.nonce === nonce),
          // map the response data a successful ClientResponse
          map((m) => {
            return {code: 200, data: m.data};
          }),
          // handle any timeouts
          timeout({
            // use a specified or default timeout
            each: responseTimeout ?? REQUEST_ACTION_DEFAULT_TIMEOUT_MS,
            with: () => {
              // report timeouts
              // this.client.reportError(timeoutResponse.error);
              // return our defined timeout ClientResponse
              return of(timeoutResponse);
            },
          }),
        ),
      );
    } else {
      // if not waiting for a response, resolve immediately
      response = Promise.resolve({code: 200});
    }

    this.parentMessageBus.post({
      type: MessageToParentType.RequestAction,
      data: requestedAction,
      nonce,
    });

    return response;
  }

  get errors() {
    return this._observability.errors ?? {};
  }

  get metrics() {
    return this._observability.metrics ?? {};
  }

  get analytics() {
    return this._observability.analytics ?? {};
  }

  get visibility() {
    return this.state.visibility ?? 'open';
  }

  getConversation(conversationId: string): Conversation | undefined {
    return this._conversationMap.get(conversationId);
  }

  async startConversation({
    conversationId,
    initialQuery,
    restart = false,
    context,
  }: {
    conversationId?: string;
    initialQuery?: string;
    restart?: boolean;
    context?: Partial<ConversationContext>;
  } = {}): Promise<Conversation | null> {
    const {currentConversationId, triggerParams: triggerParamsFromState} =
      this.state;
    const {triggerPoint: triggerPointFromConfig} = this.config;

    const currentConversation = currentConversationId
      ? this.getConversation(currentConversationId)
      : undefined;

    if (restart && !currentConversation?.isTabMostRecent()) {
      return null;
    }

    try {
      const participant: ConversationParticipant = {
        id: this.profile.visitor.id,
        role: 'user',
        displayName: this.profile.visitor.displayName || 'User',
      };

      const newTriggerPoint =
        triggerParamsFromState?.triggerPoint ??
        triggerPointFromConfig ??
        'assistant_drawer_internal';
      let triggerParams = newTriggerPoint
        ? {
            triggerPoint: newTriggerPoint,
            initialQuery,
          }
        : undefined;

      if (restart) {
        triggerParams = {
          triggerPoint:
            (this._previousTriggerPoint as SupportTriggerPointTriggerPoint) ||
            newTriggerPoint,
          initialQuery,
        };
        currentConversation?.setContextValue('supressChatEndedNotice', true);
      }

      const contextParams: ConversationContext = {
        userId: participant.id,
        audience: this.config.audience,
        entryPoint: this.config.entryPoint,
        triggerParams,
        ...context,
      };

      if (restart && currentConversation) {
        contextParams.tabs = currentConversation.tabs;
      }

      let conversationIdToAttachTo = restart ? undefined : conversationId;

      if (restart || isStartFreshTriggerPoint(triggerParams?.triggerPoint)) {
        if (currentConversation) {
          await currentConversation.leave();
        } else {
          const prevConversationResp = await this._client.inProgress({
            context: {
              ...contextParams,
              channel: 'bot',
            },
          });
          if (!('error' in prevConversationResp)) {
            if (
              prevConversationResp.data.inProgress &&
              prevConversationResp.data.conversationId
            ) {
              const {conversationId: prevConversationId} =
                prevConversationResp.data;
              await this._client.leaveConversation({
                conversationId: prevConversationId,
                participant,
              });

              if (conversationIdToAttachTo === prevConversationId) {
                conversationIdToAttachTo = undefined;
              }
            }
          }
        }
      }

      const startContextParams = restart
        ? {}
        : {
            ...contextParams,
          };

      const resp = await this._client.startConversation({
        conversationId: conversationIdToAttachTo,
        participant,
        context: {
          userId: participant.id,
          audience: this.config.audience,
          entryPoint: this.config.entryPoint,
          triggerParams,
          ...startContextParams,
        },
      });

      if ('error' in resp) {
        const errorResponse = resp as ClientErrorResponse;
        this.stateStore.setValue((state) => {
          state.error = {
            message: errorResponse.error,
            errorId: errorResponse.sentryEventId,
          };
        });
        return null;
      }

      const newConversation = resp.data.conversation;
      this._conversationMap.set(newConversation.id, newConversation);
      this.stateStore.setValue((state) => {
        state.currentConversationId = newConversation.id;
      });

      this._previousTriggerPoint =
        newConversation.context.triggerParams?.triggerPoint;

      return newConversation;
    } catch (e) {
      this.stateStore.setValue((state) => {
        state.error = {
          message: (e as Error).message,
        };
      });
      return null;
    }
  }

  async restartConversation({
    initialQuery,
  }: {initialQuery?: string} = {}): Promise<Conversation | null> {
    return this.startConversation({
      initialQuery,
      restart: true,
    });
  }

  async endConversation(): Promise<void> {
    const {currentConversationId} = this.state;
    if (currentConversationId) {
      const currentConversation = this.getConversation(currentConversationId);
      if (currentConversation) {
        await currentConversation.leave();
      }
      this._conversationMap.delete(currentConversationId);
      this.stateStore.setValue((state) => {
        state.currentConversationId = null;
      });
    }
  }

  private handleParentMessage(messageFromParent: MessageFromParent) {
    if (
      messageFromParent.type === MessageFromParentType.VisibilityChange &&
      ['open', 'closed', 'minimized'].includes(
        messageFromParent.data?.visibility,
      )
    ) {
      this.stateStore.setValue((state) => {
        state.visibility = messageFromParent.data.visibility;
        state.triggerParams = messageFromParent.data.triggerParams;
      });
    }

    if (messageFromParent.type === MessageFromParentType.CheckInitialized) {
      this.parentMessageBus.post({
        type: MessageToParentType.SetWidgetInitialized,
      });
    }

    this._parentMessageSubject.next(messageFromParent);
  }
}
